import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../core/api-client.service';
import { AuthService } from '../core/auth.service';

@Component({
  selector: 'app-google-callback-page',
  imports: [CommonModule],
  template: `
    <main class="oauth-callback">
      @if (loading()) {
        <div class="spinner" aria-hidden="true"></div>
        <h1>Google ile giriş yapılıyor</h1>
        <p>Hesabın güvenli şekilde doğrulanıyor.</p>
      } @else {
        <h1>Google girişi tamamlanamadı</h1>
        <p>{{ error() }}</p>
        <a href="/giris-yap">Giriş ekranına dön</a>
      }
    </main>
  `,
  styles: [`
    .oauth-callback{align-items:center;display:grid;gap:14px;justify-items:center;margin:0 auto;max-width:720px;min-height:55vh;padding:48px;text-align:center}.oauth-callback h1{font-family:Georgia,serif;font-size:clamp(2.2rem,6vw,4.8rem);line-height:.95;margin:0}.oauth-callback p{color:var(--muted);font-size:1.1rem}.oauth-callback a{background:var(--ink);border-radius:999px;color:var(--paper);font-weight:800;padding:14px 22px;text-decoration:none}.spinner{animation:oauth-spin .8s linear infinite;border:4px solid var(--border);border-radius:50%;border-top-color:var(--accent);height:48px;width:48px}@keyframes oauth-spin{to{transform:rotate(360deg)}}
  `]
})
export class GoogleCallbackPage implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly loading = signal(true);
  protected readonly error = signal('Google doğrulaması başarısız oldu.');

  ngOnInit(): void {
    if (typeof window === 'undefined') return;
    void this.complete();
  }

  private async complete(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    const code = params.get('code') || '';
    const providerError = params.get('error') || '';
    if (providerError || !code) {
      this.error.set(providerError || 'Google doğrulama kodu bulunamadı.');
      this.loading.set(false);
      return;
    }
    try {
      const response = await firstValueFrom(this.api.post<Record<string, unknown>>('googleLoginResult', {
        code,
        state: params.get('state') || '',
        apiCallback: true,
        previousUrl: '/'
      }));
      if (!this.isSuccessful(response)) throw new Error(String(response['message'] || 'Google girişi tamamlanamadı.'));
      const redirectLink = this.extractRedirectLink(response);
      if (redirectLink) {
        if(this.auth.completeSsoWindow(redirectLink))return;
        window.location.assign(redirectLink);
        return;
      }
      await this.auth.refresh();
      const pendingRedirect = await this.auth.continuePendingSso();
      if (pendingRedirect) {
        if(this.auth.completeSsoWindow(pendingRedirect))return;
        window.location.assign(pendingRedirect);
        return;
      }
      await this.router.navigateByUrl('/', { replaceUrl: true });
    } catch (error) {
      const source = (error as { error?: Record<string, unknown> })?.error || error as Record<string, unknown>;
      this.error.set(String(source?.['message'] || 'Google doğrulaması başarısız oldu.'));
      this.loading.set(false);
    }
  }

  private isSuccessful(response: Record<string, unknown>): boolean {
    if (response['success'] === true || response['result'] === true) return true;
    const data = response['data'];
    return !!data && typeof data === 'object' && ((data as Record<string, unknown>)['success'] === true || (data as Record<string, unknown>)['result'] === true);
  }

  private extractRedirectLink(response: unknown): string {
    const queue: unknown[] = [response];
    while (queue.length) {
      const item = queue.shift();
      if (!item || typeof item !== 'object') continue;
      if (Array.isArray(item)) { queue.push(...item); continue; }
      const record = item as Record<string, unknown>;
      if (typeof record['redirectLink'] === 'string' && /^https:\/\//i.test(record['redirectLink'])) return record['redirectLink'];
      Object.values(record).forEach((value) => { if (value && typeof value === 'object') queue.push(value); });
    }
    return '';
  }
}
